import React from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { DeveloperLnkPublisherState } from "@/const";
import { useRequest } from "@components/AppHook";
import { PublisherStateDisplay } from "@components/Displays/PublisherStateDisplay";
import { DevForm } from "@components/Forms/Fields";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, YesNoColumn } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { ModalDelete } from "@components/Modals";
import { CommonCard, ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function PublisherScreen({ screen }) {
    const { t } = useTranslation();
    const params = useParams();
    const header = t("Издатели и Разработчики");
    let body = null;
    if (params.id) {
        if (screen === "games") {
            body = <Games id={params.id} header={header} />;
        } else if (screen === "developers") {
            body = <Developers id={params.id} header={header} />;
        } else {
            body = <Edit id={params.id} header={header} />;
        }
    } else {
        body = <List header={header} />;
    }
    return <DefaultLayout>{body}</DefaultLayout>;
}

function List({ header }) {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:publisher:list"));

    if (!apiData) return;

    const headerTools = (
        <Link to={url_reverse("admin:publisher:edit", { publisher_id: "add" })} className="btn btn-sm btn-dark">
            <Awesome type="plus-circle" /> {t("Добавить")}
        </Link>
    );

    return (
        <ListingCard header={header} headerTools={headerTools}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Название")}</th>
                        <th>{t("Название для URL")}</th>
                        <th>{t("Статус")}</th>
                        <th>{t("Проверено")}</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((o, i) => {
                        return (
                            <tr key={`object_list${i}`}>
                                <td>
                                    <Link to={url_reverse("admin:publisher:edit", { publisher_id: o.id })}>
                                        {o.name}
                                    </Link>
                                </td>
                                <td>{o.slug}</td>
                                <td>
                                    <PublisherStateDisplay publisher={o} />
                                </td>
                                <td>
                                    <YesNoColumn attr={o.is_checked} />
                                </td>
                                <td>
                                    <Link to={url_reverse("admin:publisher:developers", { publisher_id: o.id })}>
                                        {t("Компании")} ({o.__developers_cnt})
                                    </Link>
                                    {" | "}
                                    <Link to={url_reverse("admin:publisher:games", { publisher_id: o.id })}>
                                        {t("Игры")} ({o.__games_cnt})
                                    </Link>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </ListingCard>
    );
}

function Edit({ id, header }) {
    const { t } = useTranslation();
    const { apiData, apiUrl } = useAppApi(url_reverse("api:publisher:edit", { publisher_id: id }));
    const { api_delete } = useRequest();
    const navigate = useNavigate();

    if (!apiData) return;

    const delPublisher = (ev) => {
        api_delete(ev, apiUrl, {
            then: (resp) => {
                if (resp.status === "ok") navigate(url_reverse("admin:publisher:list"));
            },
        });
    };

    const buttons = (
        <>
            <DevForm.Button variant="primary" type="submit" name="save">
                {t("Сохранить")}
            </DevForm.Button>
            {apiData?.allow_delete && (
                <DevForm.Button variant="danger" name="delete" className="float-right" onClick={delPublisher}>
                    {t("Удалить")}
                </DevForm.Button>
            )}
        </>
    );

    return (
        <CommonCard header={header}>
            <RenderForm backUrl={url_reverse("admin:publisher:list")} buttons={buttons} />
        </CommonCard>
    );
}

function Developers({ id, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppApi(url_reverse("api:publisher:developers", { publisher_id: id }));
    const { setDelData, setApiReget } = useAppContext();
    const { api_post } = useRequest();

    if (!apiData) return;

    const handleSubmit = ({ ev, as_who }) => {
        const state = ev.target.name;
        const lnk_id = ev.target.value;
        api_post(ev, apiUrl, {
            params: { lnk_id, state, as_who },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                }
            },
        });
    };

    const ModerButton = ({ lnk, as_who }) => {
        if (!lnk) {
            return;
        }
        if (![DeveloperLnkPublisherState.APPROVED, DeveloperLnkPublisherState.REJECTED].includes(lnk.state)) {
            return;
        }
        if (lnk.state === DeveloperLnkPublisherState.APPROVED) {
            return (
                <DevForm.Button
                    size="sm"
                    variant="warning"
                    name={DeveloperLnkPublisherState.REJECTED}
                    value={lnk.id}
                    onClick={(ev) => handleSubmit({ ev, as_who })}
                >
                    {t("Отклонить")}
                </DevForm.Button>
            );
        }
        return (
            <DevForm.Button
                size="sm"
                variant="success"
                name={DeveloperLnkPublisherState.APPROVED}
                value={lnk.id}
                onClick={(ev) => handleSubmit({ ev, as_who })}
            >
                {t("Принять")}
            </DevForm.Button>
        );
    };

    const hdr = (
        <>
            {header} -{" "}
            <a href={url_reverse("admin:publisher:edit", { publisher_id: id })} target="_blank">
                {apiData.publisher.name}
            </a>
        </>
    );

    return (
        <ListingCard header={hdr}>
            <ModalDelete />
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Компания")}</th>
                        <th>{t("Издатель")}</th>
                        <th>{t("Разработчик")}</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((o) => {
                        return (
                            <tr key={`object_list${o.id}`}>
                                <td>
                                    <Link to={url_reverse("devs:edit", { dev_id: o.id })}>{o.name}</Link>
                                </td>
                                <td>
                                    <ModerButton lnk={o.__publisher_lnk} as_who="publisher" />
                                </td>
                                <td>
                                    <ModerButton lnk={o.__gamedev_lnk} as_who="gamedev" />
                                </td>
                                <td>
                                    {o.id !== apiData.publisher.owner_id && (
                                        <DevForm.Button
                                            size="sm"
                                            variant="danger"
                                            name="del"
                                            onClick={() =>
                                                setDelData({
                                                    url:
                                                        apiUrl +
                                                        "?publisher_id=" +
                                                        apiData.publisher.id +
                                                        "&developer_id=" +
                                                        o.id,
                                                    afterRespOK: () => {
                                                        setApiReget(true);
                                                    },
                                                })
                                            }
                                            disabled={!o.__allow_delete}
                                        >
                                            {t("Удалить")}
                                        </DevForm.Button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </ListingCard>
    );
}

function Games({ id, header }) {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:publisher:games", { publisher_id: id }));

    if (!apiData) return;

    const hdr = (
        <>
            {header} -{" "}
            <a href={url_reverse("admin:publisher:edit", { publisher_id: id })} target="_blank">
                {apiData.publisher.name}
            </a>
        </>
    );

    return (
        <ListingCard header={hdr}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Игра")}</th>
                        <th>{t("Издатель")}</th>
                        <th>{t("Разработчик")}</th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((o) => {
                        return (
                            <tr key={`object_list${o.id}`}>
                                <td>
                                    <Link to={url_reverse("games:page", { game_id: o.id })}>
                                        {o.__model_locale_object.name}
                                    </Link>
                                </td>
                                <td>
                                    <YesNoColumn attr={o.__as_publisher} />
                                </td>
                                <td>
                                    <YesNoColumn attr={o.__as_gamedev} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </ListingCard>
    );
}
